/**
 * Parse directive raw strings: @directive/variable or @directive/variable:c1:c2.
 * Incomplete forms are allowed (e.g. @if-singular/, @if-singular/VarName:).
 * Matches backend ExtendedVariableNotation: ^@([\w-\(\)]+)/(\w+)(:(.*))?$
 */

export interface ParsedDirectiveRaw {
  /** Full raw string (e.g. @if-singular/LoanAge:year:years). */
  raw: string;
  /** Directive key (e.g. if-singular, if-singular(list)). */
  directive: string;
  /** Variable name, or empty if not yet chosen. */
  variable: string;
  /** Choice parts after the variable, split by : (e.g. ["year", "years"]). */
  choices: string[];
  /** True if raw starts with @ and has at least directive/ (possibly empty variable). */
  isDirective: boolean;
  /** True when variable and all required choice slots are present (for display, not validation). */
  isComplete: boolean;
}

const DIRECTIVE_RAW_REGEX = /^@([\w-()]+)\/(\w*)(?::(.*))?$/;

/**
 * Parse a variableToken raw value. If it starts with @, parse directive/variable/choices.
 * Incomplete forms: @if-singular/, @if-singular/VarName, @if-singular/VarName:, @if-singular/VarName:sing
 * are all valid; choices may be empty or partial.
 */
export const parseDirectiveRaw = (raw: string): ParsedDirectiveRaw | null => {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed.startsWith("@")) {
    return null;
  }
  const match = trimmed.match(DIRECTIVE_RAW_REGEX);
  if (!match) {
    // e.g. @ or @invalid
    return {
      raw: trimmed,
      directive: trimmed.slice(1).split("/")[0] ?? "",
      variable: "",
      choices: [],
      isDirective: true,
      isComplete: false,
    };
  }
  const directive = match[1] ?? "";
  const variable = match[2] ?? "";
  const choicesStr = match[3];
  const choices =
    choicesStr != null && choicesStr.length > 0
      ? choicesStr.split(":").map((s) => s.trim())
      : [];

  return {
    raw: trimmed,
    directive,
    variable,
    choices,
    isDirective: true,
    isComplete: false, // Caller can compare to directive config to decide completeness
  };
};

/**
 * Check if a raw string is a directive form (starts with @).
 */
export const isDirectiveRaw = (raw: string): boolean =>
  typeof raw === "string" && raw.trim().startsWith("@");

/**
 * Build raw string from parts (for updating the node after variable/choice selection).
 */
export const buildDirectiveRaw = (
  directive: string,
  variable: string,
  choices: string[],
): string => {
  const base = `@${directive}/${variable}`;
  if (choices.length === 0) return base;
  return `${base}:${choices.join(":")}`;
};
